"use client";
// Imports
import { Tabs } from "@/components";
import { useEffect, useState } from "react";
import { normaliseMyShowSlugs } from "@/lib/myShow";
import {
  RegisterLink,
  LoginLink,
} from "@kinde-oss/kinde-auth-nextjs/components";
import { Button } from "@/components/ui/button";

// My Show Drawer Content
export function MyShowDrawerContent({ myShowStore, user, showDates }) {
  const [myEventsData, setMyEventsData] = useState([]);
  const [myShowbagsData, setMyShowbagsData] = useState([]);
  const [myRidesData, setMyRidesData] = useState([]);
  const [myFoodData, setMyFoodData] = useState([]);

  const userId = user?.id ?? null;

  useEffect(() => {
    if (!myShowStore) return;

    const { myEvents, myShowbags, myFood, myRides } = myShowStore;

    const getMyShowEntries = async (slugs) => {
      try {
        const validSlugs = normaliseMyShowSlugs(slugs);
        // Separate parent slugs from sub-event slugs
        const parentSlugs = [];
        const subEventSlugs = [];

        validSlugs.forEach((slug) => {
          const subEventMatch = slug.match(/-sub-(\d+)$/);
          if (subEventMatch) {
            // Extract parent slug (remove -sub-X part)
            const parentSlug = slug.replace(/-sub-\d+$/, "");
            parentSlugs.push(parentSlug);
            subEventSlugs.push(slug);
          } else {
            parentSlugs.push(slug);
          }
        });

        // Remove duplicates from parent slugs
        const uniqueParentSlugs = [...new Set(parentSlugs)];

        const res = await fetch("/api/fetch-myshow-data", {
          method: "POST",
          body: JSON.stringify(uniqueParentSlugs),
          headers: {
            "Content-Type": "application/json",
          },
        });
        const data = await res.json();

        // Handle sub-event slugs by extracting specific day data
        if (data.events?.data) {
          const processedEvents = data.events.data
            .map((event) => {
              // Find all sub-events that belong to this parent event
              const eventSubEvents = subEventSlugs.filter((subSlug) => {
                const parentSlug = subSlug.replace(/-sub-\d+$/, "");
                return parentSlug === event.slug;
              });

              if (eventSubEvents.length > 0) {
                // Create multiple event objects for each sub-event
                return eventSubEvents
                  .map((subSlug) => {
                    const subEventMatch = subSlug.match(/-sub-(\d+)$/);
                    const dayIndex = parseInt(subEventMatch[1]);
                    const dayData = event.days?.[dayIndex];

                    if (dayData) {
                      // Create a new event object with the specific day's data
                      const processedEvent = {
                        ...event,
                        slug: subSlug, // Use the sub-event slug
                        title: dayData.override_title || event.title,
                        short_description:
                          dayData.override_short || event.short_description,
                        grid_image: dayData.override_grid || event.grid_image,
                        // Add day-specific information
                        day_date: dayData.date,
                        start_time: dayData.start_time,
                        end_time: dayData.end_time,
                        all_day: dayData.all_day_multi_day,
                        days: [dayData],
                      };

                      return processedEvent;
                    } else {
                      return null;
                    }
                  })
                  .filter(Boolean); // Remove null entries
              }

              // Return normal event data if not a sub-event
              return [event];
            })
            .flat(); // Flatten the array

          return {
            ...data,
            events: {
              ...data.events,
              data: processedEvents,
            },
          };
        }

        return data;
      } catch (err) {
        console.error("Error getting MyShow entries:", err);
      }
    };

    const fetchTabData = async () => {
      try {
        const eventsData = await getMyShowEntries(myEvents);
        setMyEventsData(eventsData.events.data);

        const showbagsData = await getMyShowEntries(myShowbags);
        setMyShowbagsData(showbagsData.showbags.data);

        const ridesData = await getMyShowEntries(myRides);
        setMyRidesData(ridesData.rides.data);

        const foodData = await getMyShowEntries(myFood);
        setMyFoodData(foodData.food.data);
      } catch (error) {
        console.error("Error fetching tab data: ", error);
      }
    };

    fetchTabData();
  }, [myShowStore]);

  if (!myShowStore) return <p>Loading...</p>;

  let tabs = [
    {
      title: "Food",
      handle: "food_outlets",
      showAsRow: false,
      data: myFoodData,
    },
    {
      title: "Showbags",
      handle: "showbags",
      showAsRow: false,
      data: myShowbagsData,
    },
    {
      title: "Rides",
      handle: "rides",
      showAsRow: false,
      data: myRidesData,
    },
    {
      title: "Events",
      handle: "events",
      showAsRow: false,
      data: myEventsData,
    },
  ];

  // Render
  return (
    <div className="overflow-y-scroll h-full">
      {!userId && (
        <div className="flex items-start py-10 mx-auto max-w-7xl">
          <div className="flex flex-col gap-5 justify-between w-full lg:gap-10 md:flex-row">
            <div className="flex flex-1 gap-5 items-center shadow-md bg-white border border-brand-fawn rounded-button-radius text-brand-espresso">
              <div className="flex flex-col gap-5 p-5 lg:p-10">
                <div className="space-y-2">
                  <h2 className="level-2">Have an account?</h2>
                  <p className="level-4">
                    Click below to login and manage your My Show itinerary.
                  </p>
                </div>

                <div className="flex space-x-2">
                  <Button
                    size="lg"
                    customColors={{
                      background: "#892102",
                      foreground: "#FFFFFF",
                    }}
                  >
                    <LoginLink>Login</LoginLink>
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex flex-1 gap-5 items-center shadow-md bg-white border border-brand-fawn rounded-button-radius text-brand-espresso">
              <div className="flex flex-col gap-5 p-5 lg:p-10">
                <div className="space-y-2">
                  <h2 className="level-2">Don't have an account?</h2>
                  <p className="level-4">
                    Create an account to start building your ultimate Show
                    itinerary!
                  </p>
                </div>

                <div className="flex space-x-2">
                  <Button
                    size="lg"
                    customColors={{
                      background: "#892102",
                      foreground: "#FFFFFF",
                    }}
                  >
                    <RegisterLink>Create Account</RegisterLink>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {userId && (
        <Tabs
          showDates={showDates}
          tabs={tabs}
          userId={userId}
          defaultToAll
        />
      )}
    </div>
  );
}
